import { SignatureRequestView } from "@/components/features/signature-request/SignatureRequestView";
import { loadPortalSignatureRequest } from "@/components/features/signature-request/signatureRequestPortalModel";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Typography,
} from "@/components/ui";
import { queryKeys } from "@/lib/data/static/query-keys";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { FileText, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const signatureFormSchema = z.object({
  passcode: z.string().min(1, { message: "Passcode is required" }),
});

type SignatureFormData = z.infer<typeof signatureFormSchema>;

export const Route = createFileRoute("/sign")({
  component: SignaturePortal,
  validateSearch: z.object({
    passcode: z.string().optional().catch(""),
  }),
});

function SignaturePortal() {
  const queryClient = useQueryClient();
  const { passcode: accessKey } = Route.useSearch();
  queryClient.removeQueries({ queryKey: [queryKeys.currentUser], exact: true }); // no need to try to authenticate
  const [passcode, setPasscode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const {
    data: portalResult,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: [queryKeys.signatureRequestPortal, passcode],
    queryFn: () => loadPortalSignatureRequest(passcode),
    enabled: !!passcode,
    staleTime: 0,
    retry: (failureCount: number, err: unknown) => {
      if (err instanceof AxiosError && err.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const form = useForm<SignatureFormData>({
    resolver: zodResolver(signatureFormSchema),
    defaultValues: {
      passcode: "",
    },
  });

  const notFound =
    !!passcode &&
    !isLoading &&
    !isFetching &&
    portalResult !== undefined &&
    !portalResult.success;

  // Handle errors in useEffect to prevent infinite re-renders
  useEffect(() => {
    if (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 401) {
          setErrorMessage(
            "Invalid passcode. Please check your passcode and try again.",
          );
        } else {
          const userMsg = error.response?.data?.meta?.userMsg;
          setErrorMessage(
            typeof userMsg === "string" && userMsg.length > 0
              ? userMsg
              : "An unexpected error occurred. Please try again.",
          );
        }
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    } else if (notFound) {
      setErrorMessage("Signature request not found");
    } else if (!isFetching && !isLoading) {
      setErrorMessage("");
    }
  }, [error, isFetching, isLoading, notFound]);

  useEffect(() => {
    if (accessKey && form.watch("passcode") !== accessKey) {
      form.setValue("passcode", accessKey);
    }
  }, [accessKey, form]);

  const onSubmit = (data: SignatureFormData) => {
    setPasscode(data.passcode);
  };

  const handleBack = () => {
    setPasscode("");
    setErrorMessage("");
    queryClient.removeQueries({
      queryKey: [queryKeys.signatureRequestPortal],
    });
    form.reset();
  };

  if (portalResult?.success === true) {
    return (
      <SignatureRequestView
        viewModel={portalResult.viewModel}
        scope={portalResult.scope}
        onBack={handleBack}
        passcode={passcode}
      />
    );
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-4 pb-6 text-center">
            <div className="bg-primary/10 mx-auto flex h-16 w-16 items-center justify-center rounded-full">
              <FileText className="text-primary h-8 w-8" />
            </div>
            <div className="space-y-2">
              <Typography variant="heading" size="lg">
                Signature request portal
              </Typography>
              <Typography variant="default" className="text-muted-foreground">
                Enter your passcode to access your signature request
              </Typography>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="passcode" className="text-sm font-medium">
                  Passcode
                </label>
                <div className="relative">
                  <Input
                    id="passcode"
                    type="text"
                    placeholder="Enter your passcode"
                    {...form.register("passcode")}
                    className="pl-10"
                  />
                  <Lock className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
                </div>
                {form.formState.errors.passcode && (
                  <Typography
                    variant="default"
                    className="text-destructive text-sm"
                  >
                    {form.formState.errors.passcode.message}
                  </Typography>
                )}
                {errorMessage && (
                  <Typography
                    variant="default"
                    className="text-destructive text-sm"
                  >
                    {errorMessage}
                  </Typography>
                )}
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isFetching}
              >
                {isLoading ? "Loading..." : "Access document"}
              </Button>
            </form>

            <div className="text-center">
              <Typography
                variant="default"
                className="text-muted-foreground text-sm"
              >
                Need help? Contact your legal representative
              </Typography>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
